import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RegisterPage = () => {
        const [step, setStep] = useState(1);
        const [customerName, setCustomerName] = useState('');
        const [firstName, setFirstName] = useState('');
        const [lastName, setLastName] = useState('');
        const [email, setEmail] = useState('');
        const [password, setPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [error, setError] = useState('');
        const [errorField, setErrorField] = useState('');
        const [success, setSuccess] = useState(false);
        const navigate = useNavigate();

        const setFieldError = (field, message) => {
                setError(message);
                setErrorField(field);
        };

        const handleStepOne = (e) => {
                e.preventDefault();
                setError('');
                setErrorField('');

                if (!customerName.trim()) {
                        setFieldError('customerName', 'Please enter your company name.');
                        return;
                }

                setStep(2);
        };

        const handleSubmit = async (e) => {
                e.preventDefault();
                setError('');
                setErrorField('');

                if (!firstName.trim()) {
                        setFieldError('firstName', 'Please enter your first name.');
                        return;
                }
                if (!lastName.trim()) {
                        setFieldError('lastName', 'Please enter your last name.');
                        return;
                }
                if (!email.trim()) {
                        setFieldError('email', 'Please enter your email address.');
                        return;
                }
                if (!password) {
                        setFieldError('password', 'Please enter a password.');
                        return;
                }
                if (password.length < 8) {
                        setFieldError('password', 'Password must be at least 8 characters long.');
                        return;
                }
                if (password !== confirmPassword) {
                        setFieldError('confirmPassword', 'Passwords do not match.');
                        return;
                }

                try {
                        const response = await fetch('/api/v2/register', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                        customer_name: customerName,
                                        first_name: firstName,
                                        last_name: lastName,
                                        email,
                                        password,
                                }),
                        });

                        if (response.status === 409) {
                                const data = await response.json();
                                const msg = data?.message ?? '';
                                if (msg.toLowerCase().includes('company')) {
                                        setStep(1);
                                        setFieldError('customerName', 'A company with this name is already registered.');
                                } else {
                                        setFieldError('email', 'An account with this email already exists.');
                                }
                                return;
                        }
                        if (!response.ok) {
                                setFieldError('email', 'Registration failed. Please try again.');
                                return;
                        }

                        setSuccess(true);
                } catch {
                        setFieldError('email', 'Registration failed. Please try again.');
                }
        };

        if (success) {
                return (
                        <div className="flex items-center justify-center h-screen bg-bg-page">
                                <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md text-center">
                                        <div className="text-green-600 text-5xl mb-4">✓</div>
                                        <h1 data-testid="register-success" className="text-2xl font-bold text-gray-900">Check your email</h1>
                                        <p className="text-gray-600">
                                                We've sent a verification link to <strong>{email}</strong>. Click the link to activate your account.
                                        </p>
                                        <button
                                                onClick={() => navigate('/login')}
                                                data-testid="register-goto-signin"
                                                className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                                        >
                                                Go to Sign In
                                        </button>
                                </div>
                        </div>
                );
        }

        return (
                <div className="flex items-center justify-center h-screen bg-bg-page">
                        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                                {/* Logo */}
                                <div className="text-center">
                                        <img src="/images/logo_text_color.png" alt="CleverPlant" className="h-10 mx-auto mb-2" />
                                </div>

                                {/* Step indicator */}
                                <div className="flex items-center justify-center space-x-2">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step >= 1 ? 'bg-brand text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                1
                                        </div>
                                        <div className={`h-0.5 w-16 transition-colors ${step >= 2 ? 'bg-brand' : 'bg-gray-200'}`} />
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step >= 2 ? 'bg-brand text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                2
                                        </div>
                                </div>

                                {/* Step 1 – Company */}
                                {step === 1 && (
                                        <form className="space-y-6" onSubmit={handleStepOne}>
                                                <div>
                                                        <h2 className="text-xl font-semibold text-gray-900 mb-1">Company information</h2>
                                                        <p className="text-sm text-gray-500">Tell us about your company</p>
                                                </div>

                                                <div>
                                                        <label htmlFor="customerName" className="text-sm font-medium text-gray-700">
                                                                Company Name
                                                        </label>
                                                        <input
                                                                id="customerName"
                                                                data-testid="register-customerName"
                                                                name="customerName"
                                                                type="text"
                                                                autoComplete="organization"
                                                                value={customerName}
                                                                onChange={(e) => setCustomerName(e.target.value)}
                                                                className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                placeholder="Enter your company name"
                                                        />
                                                        {errorField === 'customerName' && <p data-testid="register-error" data-error-field="customerName" className="text-sm text-red-600 mt-1">{error}</p>}
                                                </div>

                                                <button
                                                        type="submit"
                                                        data-testid="register-continue"
                                                        className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                                                >
                                                        Continue
                                                </button>

                                                <div className="text-center">
                                                        <button
                                                                type="button"
                                                                onClick={() => navigate('/login')}
                                                                className="text-sm text-brand hover:text-brand/80"
                                                        >
                                                                Already have an account? Sign in
                                                        </button>
                                                </div>
                                        </form>
                                )}

                                {/* Step 2 – Account details */}
                                {step === 2 && (
                                        <form className="space-y-6" onSubmit={handleSubmit}>
                                                <div>
                                                        <h2 className="text-xl font-semibold text-gray-900 mb-1">Your account details</h2>
                                                        <p className="text-sm text-gray-500">Fill in your personal information</p>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                                <label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                                                                        First Name
                                                                </label>
                                                                <input
                                                                        id="firstName"
                                                                        data-testid="register-firstName"
                                                                        type="text"
                                                                        autoComplete="given-name"
                                                                        value={firstName}
                                                                        onChange={(e) => setFirstName(e.target.value)}
                                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                        placeholder="Enter your first name"
                                                                />
                                                                {errorField === 'firstName' && <p data-testid="register-error" data-error-field="firstName" className="text-sm text-red-600 mt-1">{error}</p>}
                                                        </div>
                                                        <div>
                                                                <label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                                                                        Last Name
                                                                </label>
                                                                <input
                                                                        id="lastName"
                                                                        data-testid="register-lastName"
                                                                        type="text"
                                                                        autoComplete="family-name"
                                                                        value={lastName}
                                                                        onChange={(e) => setLastName(e.target.value)}
                                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                        placeholder="Enter your last name"
                                                                />
                                                                {errorField === 'lastName' && <p data-testid="register-error" data-error-field="lastName" className="text-sm text-red-600 mt-1">{error}</p>}
                                                        </div>
                                                </div>

                                                <div>
                                                        <label htmlFor="email" className="text-sm font-medium text-gray-700">
                                                                Email
                                                        </label>
                                                        <input
                                                                id="email"
                                                                data-testid="register-email"
                                                                name="email"
                                                                type="email"
                                                                autoComplete="email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                placeholder="Enter your email"
                                                        />
                                                        {errorField === 'email' && <p data-testid="register-error" data-error-field="email" className="text-sm text-red-600 mt-1">{error}</p>}
                                                </div>

                                                <div>
                                                        <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                                                Password
                                                        </label>
                                                        <input
                                                                id="password"
                                                                data-testid="register-password"
                                                                name="password"
                                                                type="password"
                                                                autoComplete="new-password"
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                placeholder="Min. 8 characters"
                                                        />
                                                        {errorField === 'password' && <p data-testid="register-error" data-error-field="password" className="text-sm text-red-600 mt-1">{error}</p>}
                                                </div>

                                                <div>
                                                        <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                                                                Confirm Password
                                                        </label>
                                                        <input
                                                                id="confirmPassword"
                                                                data-testid="register-confirmPassword"
                                                                name="confirmPassword"
                                                                type="password"
                                                                autoComplete="new-password"
                                                                value={confirmPassword}
                                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                                className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                                placeholder="Enter password again"
                                                        />
                                                        {errorField === 'confirmPassword' && <p data-testid="register-error" data-error-field="confirmPassword" className="text-sm text-red-600 mt-1">{error}</p>}
                                                </div>

                                                <div className="flex gap-3">
                                                        <button
                                                                type="button"
                                                                onClick={() => { setStep(1); setError(''); setErrorField(''); }}
                                                                data-testid="register-back"
                                                                className="flex justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                                        >
                                                                Back
                                                        </button>
                                                        <button
                                                                type="submit"
                                                                data-testid="register-submit"
                                                                className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                                                        >
                                                                Create Account
                                                        </button>
                                                </div>

                                                <div className="text-center">
                                                        <button
                                                                type="button"
                                                                onClick={() => navigate('/login')}
                                                                className="text-sm text-brand hover:text-brand/80"
                                                        >
                                                                Already have an account? Sign in
                                                        </button>
                                                </div>
                                        </form>
                                )}
                        </div>
                </div>
        );
};

export default RegisterPage;
